import { createContext, use, useMemo } from 'react'
import { RouterProvider, createBrowserRouter } from 'react-router'
import Root from './components/Root'
import AppLauncher from './pages/AppLauncher'
import Consent from './pages/Consent'
import AcceptInvitation from './containers/AcceptInvitation'
import PasswordReset from './containers/PasswordReset'
import PasswordResetConfirmation from './containers/PasswordResetConfirmation'
import SetupMFAEnforced from './containers/SetupMFAEnforced'
import SetupMFA from './containers/SetupMFA'
import SetupMFATOTP from './containers/SetupMFATOTP'
import SetupMFAWebAuthn from './containers/SetupMFAWebAuthn'
import {
  ACCEPT_INVITATION,
  APP_LAUNCHER,
  CONSENT,
  LOGIN_ROOT,
  PASSWORD_RESET,
  PASSWORD_RESET_CONFIRMATION,
  SETUP_MFA,
  SETUP_MFA_ENFORCED,
  SETUP_TOTP,
  SETUP_WEB_AUTHN,
} from './constants/routes'
import emptyBrandingSettings from './constants/emptyBrandingSettings'

const AppPropsContext = createContext(/** @type {Record<string, any>} */ ({}))

const useAppProps = () => use(AppPropsContext)

const RootRoute = () => {
  const props = useAppProps()
  const brandingSettings = props.brandingSettings || emptyBrandingSettings
  return (
    <Root
      csrf={props.csrf}
      localLoginEnabled={props.localLoginEnabled}
      identityProviders={props.identityProviders}
      showCaptcha={props.showCaptcha}
      showGoogleFooterLink={props.showGoogleFooterLink}
      disclaimerEnabled={props.disclaimerEnabled}
      siteKey={props.siteKey}
      brandAssetsURL={props.brandAssetsURL}
      talkdeskIdAssetsURL={props.talkdeskIdAssetsURL}
      marketingBannerURL={props.marketingBannerURL}
      marketingBannerTimeout={props.marketingBannerTimeout}
      brandingSettings={brandingSettings}
      metadata={props.metadata}
      sidebannerEnabled={props.sidebannerEnabled}
      email={props.email}
    />
  )
}

const AppLauncherRoute = () => {
  const props = useAppProps()
  const brandingSettings = props.brandingSettings || emptyBrandingSettings
  return (
    <AppLauncher
      metadata={props.metadata}
      brandAssetsURL={props.brandAssetsURL}
      brandingSettings={brandingSettings}
    />
  )
}

const ConsentRoute = () => {
  const props = useAppProps()
  const brandingSettings = props.brandingSettings || emptyBrandingSettings
  return (
    <Consent
      metadata={props.metadata}
      csrf={props.csrf}
      brandingSettings={brandingSettings}
    />
  )
}

const AcceptInvitationRoute = () => {
  const props = useAppProps()
  return <AcceptInvitation siteKey={props.siteKey} />
}

const PasswordResetRoute = () => {
  const props = useAppProps()
  return <PasswordReset siteKey={props.siteKey} />
}

const PasswordResetConfirmationRoute = () => {
  const props = useAppProps()
  return <PasswordResetConfirmation siteKey={props.siteKey} />
}

const SetupMFAEnforcedRoute = () => {
  const props = useAppProps()
  return <SetupMFAEnforced user={props.user} />
}

const RouterRoot = (props) => {
  const router = useMemo(
    () =>
      createBrowserRouter([
        { path: LOGIN_ROOT, element: <RootRoute /> },
        { path: APP_LAUNCHER, element: <AppLauncherRoute /> },
        { path: CONSENT, element: <ConsentRoute /> },
        { path: ACCEPT_INVITATION, element: <AcceptInvitationRoute /> },
        { path: PASSWORD_RESET, element: <PasswordResetRoute /> },
        {
          path: PASSWORD_RESET_CONFIRMATION,
          element: <PasswordResetConfirmationRoute />,
        },
        { path: SETUP_MFA_ENFORCED, element: <SetupMFAEnforcedRoute /> },
        { path: SETUP_MFA, element: <SetupMFA /> },
        { path: SETUP_TOTP, element: <SetupMFATOTP /> },
        { path: SETUP_WEB_AUTHN, element: <SetupMFAWebAuthn /> },
      ]),
    [],
  )

  return (
    <AppPropsContext value={props}>
      <RouterProvider router={router} />
    </AppPropsContext>
  )
}

export default RouterRoot
